import { defineStore } from 'pinia';
import { getPatentsOfUser, updatePatent } from '@/services/patents.js';
import { getOfficeActionsOfPatent, updateOfficeAction } from '@/services/office-action.js';
import { getDocketTasksByPatentIdByDeadline, updateDocketTask } from '@/services/docketing.js';
import { getLogsByApplicationId, updateLog } from '@/services/logging.js';
import { db } from '@/services/db.js';

export const usePatentStore = defineStore('patent', {
  state: () => ({
    firmId: null,
    userId: null,
    all_patents: [],
    currentPatentId: '',
    currentPatentXml: "",
    currentPatentSpecXml: "",
    currentPatentClaimSets: [],
    currentPatentOaRemarksSets: [],
    loaded: false,
    sort_column: null,
    unsubscribePatents: null,
    tableColumns: [
        {
          name: "Application No.",
          sortStatus: "a",
          checked: true,
          fieldName: "applId",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "Docket No.",
          sortStatus: "a",
          checked: true,
          fieldName: "appAttrDockNumber",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "Title",
          sortStatus: "a",
          checked: true,
          fieldName: "patentTitle",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "Client",
          sortStatus: "a",
          checked: true,
          fieldName: "clientName",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "First Applicant",
          sortStatus: "a",
          checked: true,
          fieldName: "firstNamedApplicant",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "First Inventor",
          sortStatus: "a",
          checked: true,
          fieldName: "inventorName",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "App Status",
          sortStatus: "a",
          checked: true,
          fieldName: "appStatus",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "Responsible Member",
          sortStatus: "a",
          checked: true,
          fieldName: "assignedMembers",
          filterStr: "",
          filterFlag: false,
        },
        {
          name: "Customer No.",
          sortStatus: "a",
          checked: true,
          fieldName: "appCustNumber",
          filterStr: "",
          filterFlag: false,
        }],
  }),
  //GOTCHA: don't name getters the same as the state variable.  it gets confused
  getters: {
    patents: (state) => state.all_patents,
    currentPatent: (state) => {
      if (state.currentPatentId == null) {
        return null;
      }
      return state.all_patents.find((pat) => pat.applId === state.currentPatentId);
    },
    pending_patents: (state) => {
      return state.all_patents
        .filter((x) => !x.archived &&
        !x.patentNumber &&
        x.appStatus &&
        !x.appStatus.toLowerCase().includes("abandoned") &&
        !x.appStatus.toLowerCase().includes("expired") &&
        x.appStatus.toLowerCase() !== "patented case"
      );
    },
    archived_patents: (state) => {
      return state.all_patents
        .filter((x) => x.archived);
    },
    other_patents: (state) => {
        return state.all_patents
          .filter((x) => !x.archived && (x.patentNumber ||
          x.appStatus.toLowerCase().includes("abandoned") ||
          x.appStatus.toLowerCase().includes("expired")) ||
          x.appStatus.toLowerCase() === "patented case"
        );
    },
    columns: (state) => state.tableColumns,
    sortColumn: (state) => state.sort_column,

  },
  actions: {
    updateCurrentPatent(patentId) {
      if (this.currentPatentId === patentId) {
        return;
      }
      this.currentPatentId = patentId;
      this.currentPatentClaimSets = [];
      this.currentPatentXml = "";
      this.currentPatentSpecXml = "";
      this.currentPatentOaRemarksSets = [];
    },
    updateClientOfPatent(patentId, clientId) {
      return updatePatent(this.firmId, patentId, { "client": clientId});
    },
    async updateAssigned(patentId, assigned) {
      const firmId = this.firmId;
      try {
        await db.runTransaction(async (transaction) => {
          // Update the patent
          await updatePatent(firmId, patentId, { "assigned": assigned }, transaction);

          // Get all office actions of the patent
          const officeActions = await getOfficeActionsOfPatent(firmId, patentId);
          for (const officeAction of officeActions.value) {
            await updateOfficeAction(firmId, officeAction.id, { "assigned": assigned }, transaction);
          }

          // get all docket tasks of the patent
          const docketTasks = await getDocketTasksByPatentIdByDeadline(firmId, patentId);
          for (const docketTask of docketTasks.value) {
            await updateDocketTask(firmId, docketTask.id, { "assigned": assigned }, transaction);
          }

          // Get all logs of the patent
          const logs = await getLogsByApplicationId(firmId, patentId);
          for (const log of logs.value) {
            await updateLog(firmId, log.id, { "assigned": assigned }, transaction);
          }
        });
      } catch (error) {
        console.error("Transaction failed: ", error);
        throw error;
      }
    },
    async updateArchived(patentId, archived) {
      const firmId = this.firmId;
      try {
        await db.runTransaction(async (transaction) => {
          // Update the patent
          await updatePatent(firmId, patentId, { "archived": archived }, transaction);

          // Get all office actions of the patent
          const officeActions = await getOfficeActionsOfPatent(firmId, patentId);
          for (const officeAction of officeActions.value) {
            await updateOfficeAction(firmId, officeAction.id, { "archived": archived }, transaction);
          }

          // get all docket tasks of the patent
          const docketTasks = await getDocketTasksByPatentIdByDeadline(firmId, patentId);
          for (const docketTask of docketTasks.value) {
            await updateDocketTask(firmId, docketTask.id, { "archived": archived, "status": "To Do" }, transaction);
          }

          // Get all logs of the patent
          const logs = await getLogsByApplicationId(firmId, patentId);
          for (const log of logs.value) {
            await updateLog(firmId, log.id, { "archived": archived }, transaction);
          }
        });
      } catch (error) {
        console.error("Transaction failed: ", error);
        throw error;
      }
    },
    // get patent by id from all_patents
    getPatentById(patentId) {
      return this.all_patents.find((pat) => pat.applId === patentId);
    },
    updateColumns(newColumns) {
      this.tableColumns = newColumns;
    },

    updateSortColumn(newVal) {
      this.sort_column = newVal;
    },
    async loadPatents(userId, firmId) {
      if (!firmId || !userId || firmId == "" || userId == "") {
        throw new Error('firm and user required');
      }
      const columnChecks = JSON.parse(localStorage.getItem("patent_columns_checked"));
      if (columnChecks && Array.isArray(columnChecks)) {
        this.tableColumns.forEach((c, index) => { c.checked = columnChecks[index]; });
      }

      if (firmId != this.firmId || userId != this.userId) {
        // Unsubscribe from the previous listener if it exists
        if (this.unsubscribePatents && typeof this.unsubscribePatents === 'function') {
          this.unsubscribePatents();
        }
        this.loaded = false;
        this.firmId = firmId;
        this.userId = userId;
        this.all_patents = [];

        const { patents, unsubscribe } = await getPatentsOfUser(firmId, userId);
        this.loaded = true;
        this.all_patents = patents;

        // Store the unsubscribe function to call it later when needed
        this.unsubscribePatents = unsubscribe;
      }
    },
    setCurrentPatentXml(xml) {
      this.currentPatentXml = xml;
    },
    setCurrentPatentSpecXml(xml) {
      this.currentPatentSpecXml = xml;
    },
    setCurrentPatentClaimSets(claimSets) {
      this.currentPatentClaimSets = claimSets;
    },
    setCurrentPatentOaRemarksSets(oaRemarksSets) {
      this.currentPatentOaRemarksSets = oaRemarksSets;
    },
  },

});

